import { AlertBanner } from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ASDSThemeProvider } from "~/components/designSystem/ASDSThemeProvider";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import {
  useGetAutostoreGridConnectionModeQuery,
  useGetAutostoreGridStatusQuery
} from "~/redux/warehouse/autostoreGrid.hooks";

import { useAppSelector } from "./store";

interface GridConnectionProps {
  okPollingRate?: number;
  errorPollingRate?: number;
}

const DEFAULT_POLLING_RATES = {
  OK: 15_000,
  ERROR: 5_000
};

export const GridConnection = ({
  okPollingRate = DEFAULT_POLLING_RATES.OK,
  errorPollingRate = DEFAULT_POLLING_RATES.ERROR
}: GridConnectionProps = {}) => {
  const { t } = useTranslation();
  const thisWorkstation = useAppSelector(selectThisWorkstation);

  const maxFailureCount = 3;
  const [failureCount, setFailureCount] = useState(0);

  const pollingRate = failureCount > 0 ? errorPollingRate : okPollingRate;
  const shouldShowAlert = failureCount >= maxFailureCount;

  const { data: gridConnection } = useGetAutostoreGridConnectionModeQuery(
    thisWorkstation ? thisWorkstation.autostoreGridId : skipToken
  );

  const {
    data: gridStatus,
    isFetching,
    isSuccess,
    isError,
    requestId
  } = useGetAutostoreGridStatusQuery(
    thisWorkstation ? thisWorkstation.autostoreGridId : skipToken,
    {
      pollingInterval:
        gridConnection?.connectionMode === "Active" ? 0 : pollingRate
    }
  );

  useEffect(() => {
    if (isFetching || (!isSuccess && !isError)) return;

    const hasError =
      isError ||
      (gridStatus &&
        (gridStatus.kind === "error" || gridStatus.mode !== "Running"));

    if (hasError) {
      setFailureCount((prev) => prev + 1);
    } else {
      setFailureCount(0);
    }
  }, [requestId, gridStatus, isSuccess, isError, isFetching]);

  return (
    <>
      {thisWorkstation &&
        gridStatus &&
        shouldShowAlert &&
        (gridStatus.kind === "error" || gridStatus.mode !== "Running") && (
          <ASDSThemeProvider>
            <AlertBanner
              severity="warning"
              sx={{
                width: "100%",
                whiteSpace: "pre-line",
                borderRadius: 0
              }}
            >
              {gridStatus.kind === "error"
                ? `${t("grid error")} ${gridStatus.autostoreFaultCode}: ${t(`autostoreFault.${gridStatus.autostoreFaultCode}`, { defaultValue: gridStatus.message })}`
                : `${t("grid mode")}: ${gridStatus.mode}`}
            </AlertBanner>
          </ASDSThemeProvider>
        )}
    </>
  );
};
